import type { ReactNode } from 'react';

import { glacierAssistantApiSubmitFeedback } from '@/lib/heyapi/sdk.gen';

import type { ChatMessage as ChatMessageType } from './useChatStore';
import { useChatStore } from './useChatStore';

function renderInline(text: string): ReactNode {
  const parts: ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Escaped characters (e.g., \* \` \#)
    if (remaining.startsWith('\\') && remaining.length > 1) {
      parts.push(remaining[1]);
      remaining = remaining.slice(2);
      continue;
    }

    // Inline code
    const codeMatch = remaining.match(/^`([^`]+)`/);
    if (codeMatch) {
      parts.push(
        <code key={key++} className="rounded bg-gray-200 px-1 py-0.5 text-xs">
          {codeMatch[1]}
        </code>
      );
      remaining = remaining.slice(codeMatch[0].length);
      continue;
    }

    // Bold + Italic with ***
    const boldItalicMatch = remaining.match(/^\*\*\*(.+?)\*\*\*/);
    if (boldItalicMatch) {
      parts.push(
        <strong key={key++} className="font-semibold italic">
          {boldItalicMatch[1]}
        </strong>
      );
      remaining = remaining.slice(boldItalicMatch[0].length);
      continue;
    }

    // Bold with **
    const boldMatch = remaining.match(/^\*\*(.+?)\*\*/);
    if (boldMatch) {
      parts.push(
        <strong key={key++} className="font-semibold">
          {boldMatch[1]}
        </strong>
      );
      remaining = remaining.slice(boldMatch[0].length);
      continue;
    }

    // Italic with single *
    const italicMatch = remaining.match(/^\*([^*]+)\*/);
    if (italicMatch) {
      parts.push(<em key={key++}>{italicMatch[1]}</em>);
      remaining = remaining.slice(italicMatch[0].length);
      continue;
    }

    // Strikethrough with ~~
    const strikeMatch = remaining.match(/^~~(.+?)~~/);
    if (strikeMatch) {
      parts.push(
        <span key={key++} className="line-through">
          {strikeMatch[1]}
        </span>
      );
      remaining = remaining.slice(strikeMatch[0].length);
      continue;
    }

    // Links [text](url)
    const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
    if (linkMatch) {
      parts.push(
        <a
          key={key++}
          href={linkMatch[2]}
          className="text-emerald-700 underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          {linkMatch[1]}
        </a>
      );
      remaining = remaining.slice(linkMatch[0].length);
      continue;
    }

    // Find next special char
    const nextSpecial = remaining.search(/[`*~[\\\n]/);
    if (nextSpecial === -1) {
      parts.push(remaining);
      break;
    } else if (nextSpecial === 0) {
      parts.push(remaining[0]);
      remaining = remaining.slice(1);
    } else {
      parts.push(remaining.slice(0, nextSpecial));
      remaining = remaining.slice(nextSpecial);
    }
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>;
}

function isTableRow(line: string): boolean {
  return line.trim().startsWith('|') && line.trim().endsWith('|');
}

function isTableSeparator(line: string): boolean {
  return /^\|[\s:]*[-]+[\s:]*(\|[\s:]*[-]+[\s:]*)*\|$/.test(line.trim());
}

function parseTableCells(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

function renderTable(tableLines: string[], startIndex: number): ReactNode {
  const headerCells = parseTableCells(tableLines[0]);
  const dataRows = tableLines.slice(2); // skip header and separator

  return (
    <div key={startIndex} className="my-2 overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="border-b border-gray-300 bg-gray-50">
            {headerCells.map((cell, j) => (
              <th key={j} className="px-2 py-1 text-left font-semibold">
                {renderInline(cell)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, j) => {
            const cells = parseTableCells(row);
            return (
              <tr key={j} className="border-b border-gray-200">
                {cells.map((cell, k) => (
                  <td key={k} className="px-2 py-1">
                    {renderInline(cell)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function renderMarkdown(text: string) {
  const lines = text.split('\n');
  const elements: ReactNode[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Table detection: look for header row + separator + data rows
    if (
      isTableRow(line) &&
      i + 1 < lines.length &&
      isTableSeparator(lines[i + 1])
    ) {
      const tableLines: string[] = [line, lines[i + 1]];
      let j = i + 2;
      while (j < lines.length && isTableRow(lines[j])) {
        tableLines.push(lines[j]);
        j++;
      }
      elements.push(renderTable(tableLines, i));
      i = j - 1; // skip past all table lines
      continue;
    }

    // H4
    if (line.startsWith('#### ')) {
      elements.push(
        <h4 key={i} className="mb-1 mt-2 text-sm font-semibold">
          {renderInline(line.slice(5))}
        </h4>
      );
      continue;
    }

    // H3
    if (line.startsWith('### ')) {
      elements.push(
        <h3 key={i} className="mb-1 mt-2 text-sm font-semibold">
          {renderInline(line.slice(4))}
        </h3>
      );
      continue;
    }

    // H2
    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={i} className="mb-1 mt-3 text-sm font-bold">
          {renderInline(line.slice(3))}
        </h2>
      );
      continue;
    }

    // H1
    if (line.startsWith('# ')) {
      elements.push(
        <h1 key={i} className="mb-2 text-base font-bold">
          {renderInline(line.slice(2))}
        </h1>
      );
      continue;
    }

    // Horizontal rule
    if (/^[-*_]{3,}$/.test(line.trim())) {
      elements.push(<hr key={i} className="my-2 border-gray-300" />);
      continue;
    }

    // Blockquote (supports nested >)
    if (line.startsWith('> ')) {
      elements.push(
        <blockquote
          key={i}
          className="my-1 border-l-2 border-gray-300 pl-3 italic text-gray-600"
        >
          {renderInline(line.slice(2))}
        </blockquote>
      );
      continue;
    }

    // Ordered list item (with possible indentation)
    const olMatch = line.match(/^(\s*)(\d+)\.\s+(.*)/);
    if (olMatch) {
      const indent = olMatch[1].length > 0 ? 'pl-4' : '';
      elements.push(
        <div key={i} className={`mb-1 flex gap-2 ${indent}`}>
          <span className="shrink-0 text-gray-500">{olMatch[2]}.</span>
          <span>{renderInline(olMatch[3])}</span>
        </div>
      );
      continue;
    }

    // Unordered list item (with possible indentation)
    const ulMatch = line.match(/^(\s*)[-*+]\s+(.*)/);
    if (ulMatch) {
      const indent = ulMatch[1].length > 0 ? 'pl-4' : '';
      elements.push(
        <div key={i} className={`mb-1 flex gap-2 pl-1 ${indent}`}>
          <span className="shrink-0 text-gray-400">&bull;</span>
          <span>{renderInline(ulMatch[2])}</span>
        </div>
      );
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      elements.push(<div key={i} className="h-1" />);
      continue;
    }

    // Regular text
    elements.push(
      <p key={i} className="mb-1">
        {renderInline(line)}
      </p>
    );
  }

  return elements;
}

async function sendFeedback(
  interactionId: number,
  feedback: 1 | -1
): Promise<void> {
  await glacierAssistantApiSubmitFeedback({
    path: { interaction_id: interactionId },
    body: { feedback },
  });
}

function FeedbackButtons({
  interactionId,
  currentFeedback,
}: {
  interactionId: number;
  currentFeedback?: 1 | -1;
}) {
  const setFeedback = useChatStore((s) => s.setFeedback);

  const handleFeedback = async (feedback: 1 | -1) => {
    setFeedback(interactionId, feedback);
    await sendFeedback(interactionId, feedback);
  };

  return (
    <div className="mt-1 flex gap-1">
      <button
        onClick={() => handleFeedback(1)}
        className={`rounded p-0.5 text-xs ${
          currentFeedback === 1
            ? 'text-emerald-700'
            : 'text-gray-400 hover:text-gray-600'
        }`}
        aria-label="Thumbs up"
        title="Helpful"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
        </svg>
      </button>
      <button
        onClick={() => handleFeedback(-1)}
        className={`rounded p-0.5 text-xs ${
          currentFeedback === -1
            ? 'text-red-600'
            : 'text-gray-400 hover:text-gray-600'
        }`}
        aria-label="Thumbs down"
        title="Not helpful"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M18 9.5a1.5 1.5 0 11-3 0v-6a1.5 1.5 0 013 0v6zM14 9.667v-5.43a2 2 0 00-1.106-1.79l-.05-.025A4 4 0 0011.057 2H5.64a2 2 0 00-1.962 1.608l-1.2 6A2 2 0 004.44 12H8v4a2 2 0 002 2 1 1 0 001-1v-.667a4 4 0 01.8-2.4l1.4-1.866a4 4 0 00.8-2.4z" />
        </svg>
      </button>
    </div>
  );
}

export function ChatMessage({ message }: { message: ChatMessageType }) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div>
        <div
          className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
            isUser ? 'bg-emerald-700 text-white' : 'bg-gray-100 text-gray-900'
          }`}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap">{message.content}</p>
          ) : (
            <div>{renderMarkdown(message.content)}</div>
          )}
        </div>
        {!isUser &&
        message.interactionId != null &&
        message.interactionId > 0 ? (
          <FeedbackButtons
            interactionId={message.interactionId}
            currentFeedback={message.feedback}
          />
        ) : null}
      </div>
    </div>
  );
}
